import { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { commentService } from '../services/api';
import 'animate.css';
import TrackVisibility from 'react-on-screen';

export const CommentsSection = () => {
  const [comments, setComments] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    comment: ''
  });
  const [formStatus, setFormStatus] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadComments();
  }, []);

  const loadComments = async () => {
    const data = await commentService.getComments(false);
    setComments(data);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormStatus({});

    try {
      await commentService.submitComment(
        formData.name,
        formData.email,
        formData.comment
      );
      
      setFormStatus({ 
        success: true, 
        message: 'Thank you! Your comment has been submitted and is awaiting approval.' 
      });
      setFormData({ name: '', email: '', comment: '' });
      
      // Reload comments after a delay
      setTimeout(loadComments, 1000);
    } catch (error) {
      setFormStatus({ 
        success: false, 
        message: 'Failed to submit comment. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <section className="comments-section" id="comments">
      <Container>
        <Row className="justify-content-center">
          <Col lg={10}>
            <TrackVisibility>
              {({ isVisible }) =>
                <div className={isVisible ? "animate__animated animate__fadeIn" : ""}>
                  <h2>Leave a Comment</h2>
                  <p className="comments-intro">Share your thoughts, feedback, or just say hello!</p>

                  {/* Comment Form */}
                  <div className="comment-form-container">
                    <Form onSubmit={handleSubmit}>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Control
                              type="text"
                              name="name"
                              aria-label="Your name"
                              placeholder="Your Name *"
                              value={formData.name}
                              onChange={handleChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Control
                              type="email"
                              name="email"
                              aria-label="Your email (optional)"
                              placeholder="Your Email (optional)"
                              value={formData.email}
                              onChange={handleChange}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mb-3">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="comment"
                          aria-label="Your comment"
                          placeholder="Your Comment *"
                          value={formData.comment}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                      
                      {formStatus.message && (
                        <div role="status" className={`form-message ${formStatus.success ? 'success' : 'error'}`}>
                          {formStatus.message}
                        </div>
                      )}
                      
                      <Button type="submit" disabled={loading} className="submit-comment-btn">
                        {loading ? 'Submitting...' : 'Submit Comment'}
                      </Button>
                    </Form>
                  </div>

                  {/* Comments Display */}
                  <div className="comments-display">
                    <h3>Comments ({comments.length})</h3>
                    {comments.length === 0 ? (
                      <p className="no-comments">No comments yet. Be the first to comment!</p>
                    ) : (
                      <div className="comments-list">
                        {comments.map((comment) => (
                          <div key={comment.commentId} className="comment-card">
                            <div className="comment-header">
                              <div className="comment-avatar">
                                {comment.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="comment-meta">
                                <h4 className="comment-author">{comment.name}</h4>
                                <span className="comment-date">{formatDate(comment.timestamp)}</span>
                              </div>
                            </div>
                            <p className="comment-text">{comment.comment}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              }
            </TrackVisibility>
          </Col>
        </Row>
      </Container>
    </section>
  );
};
